import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ChevronRight, Search, Star, History, BarChart3, Lightbulb, ArrowRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/ui/PageHeader";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { EquipmentInstallsReport } from "./EquipmentInstallsReport";
import { SerialLookupReport } from "./SerialLookupReport";
import { WarrantyExpiringReport } from "./WarrantyExpiringReport";
import { RefrigerantReport } from "./RefrigerantReport";
import { StockCountsReport } from "./StockCountsReport";
import { MembershipRenewalsReport } from "./MembershipRenewalsReport";
import { TuneupsDueReport } from "./TuneupsDueReport";
import { TruckPartsReport } from "./TruckPartsReport";
import { SeasonalDemandReport } from "./SeasonalDemandReport";
import { ClassificationReview } from "./ClassificationReview";
import { LeadSourceReport } from "./LeadSourceReport";
import { RevenueReport } from "./RevenueReport";
import { MarginReport } from "./MarginReport";
import { DispatchReport } from "./DispatchReport";
import { CustomReport } from "./CustomReport";
import { InsightsPanel } from "./InsightsPanel";
import { DispatchListReport } from "./lists/DispatchListReport";
import { InvoiceListReport } from "./lists/InvoiceListReport";
import { PurchaseOrderListReport } from "./lists/PurchaseOrderListReport";
import { TicketListReport } from "./lists/TicketListReport";
import { TechPayrollReport } from "./lists/TechPayrollReport";
import { CollectionsReport } from "./lists/CollectionsReport";
import { CustomersReport } from "./lists/CustomersReport";
import { VendorListReport } from "./lists/VendorListReport";
import { AskPegasus } from "./AskPegasus";
import { useDepartments } from "./api";
import { SECTIONS, REPORTS, type ReportKey, type ReportCard } from "./catalog";

// ============================================================================
// REPORT-01 — the Reports surface. A picker of reports + the active report.
// First live report: Equipment installs (the demand-history half of the
// inventory-forecast vision). The greyed cards are the roadmap, shown so the
// surface reads as a growing reporting home, not a one-off — never faked.
// ============================================================================


const COLLAPSED_KEY = "pegasus.reports.collapsedSections";
// REPORTS-IA v3 (Seth 2026-08-18: "the reporting tab has become a mess"):
// the mess was FINDABILITY at 21+ reports, not structure. Three additions:
// type-to-filter search, star-to-pin favorites (top of the rail), and
// automatic recents. All remembered per browser.
const PINNED_KEY = "pegasus.reports.pinned";
const RECENTS_KEY = "pegasus.reports.recents";

function loadKeys(storageKey: string): string[] {
  try { return JSON.parse(localStorage.getItem(storageKey) ?? "[]"); } catch { return []; }
}
function saveKeys(storageKey: string, keys: string[]) {
  try { localStorage.setItem(storageKey, JSON.stringify(keys)); } catch { /* ignore */ }
}

export function ReportsView() {
  // PERM-05 (Seth 2026-06-12): every report is its own capability —
  // reports.<key> — so the owner picks which users see which reports.
  // Dynamic keys in a loop, so we use can() directly (not one useCan each).
  const { can } = usePermissions();
  const allowed = (key: string) => can(`reports.${key}`);
  // Load the live ESC department names (mirror_department) into the deptLabel
  // cache so Revenue/Margin/Dispatch reports show authoritative line-of-business
  // labels instead of the hardcoded fallback.
  useDepartments();

  // The selected report lives in the URL (?report=…) so Back from a customer
  // page returns to the SAME report, not the default (Seth's nav bug,
  // 2026-06-10) — and report links become shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  // REPORTS-HOME-02: /reports opens on Ask Pegasus (the briefing rides
  // underneath it) — Seth 2026-09-02. The rail's first allowed row is the
  // fallback for anyone who can't run it.
  const urlKey = searchParams.get("report") ?? "ask";
  const firstAllowed =
    REPORTS.find((r) => r.live && allowed(r.key))?.key ?? "ask";
  const selected: ReportKey = (REPORTS.some(
    (r) => r.key === urlKey && r.live && allowed(r.key),
  )
    ? urlKey
    : firstAllowed) as ReportKey;
  const [pinned, setPinned] = useState<string[]>(() => loadKeys(PINNED_KEY));
  const [recents, setRecents] = useState<string[]>(() => loadKeys(RECENTS_KEY));
  const [railQuery, setRailQuery] = useState("");

  const setSelected = (k: ReportKey) => {
    const next = new URLSearchParams(searchParams);
    next.set("report", k);
    setSearchParams(next, { replace: false });
    // Track recents (most recent first, deduped, capped at 4).
    setRecents((cur) => {
      const nextR = [k, ...cur.filter((x) => x !== k)].slice(0, 4);
      saveKeys(RECENTS_KEY, nextR);
      return nextR;
    });
  };
  const togglePin = (k: string) => {
    setPinned((cur) => {
      const nextP = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
      saveKeys(PINNED_KEY, nextP);
      return nextP;
    });
  };

  // Section headers are CLICKABLE — collapse/expand groups to organize the
  // rail as the report list grows (Seth 2026-06-12); remembered per browser.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? "[]"));
    } catch {
      return new Set();
    }
  });
  const toggleSection = (label: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // ignore
      }
      return next;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="w-full px-6 py-8"
    >
      <PageHeader
        icon={BarChart3}
        title="Reports"
        description="Slice the book any way you need, then export or schedule it. The deep, any-field analysis lives here — the worklist tabs stay lean."
      />

      {/* Report picker */}
      {/* REPORTS-NAV v2 (Seth 2026-06-10: the card grid got "ridiculously
          long and big"): a compact LEFT RAIL — sections as quiet group
          labels, one row per report, active highlighted. The Looker/Metabase/
          ServiceTitan reporting-home pattern: scales to dozens of reports
          without eating the screen; the report itself gets the width. */}
      <div className="flex items-start gap-6">
        {/* top-20 = 16px below the floating glass top bar (DESKTOP-GLASS-01). */}
        <nav className="sticky top-20 w-56 shrink-0 space-y-5">
          {/* Type-to-find — at 21+ reports this beats scanning (REPORTS-IA v3). */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={railQuery}
              onChange={(e) => setRailQuery(e.target.value)}
              placeholder="Find a report…"
              className="h-9 w-full rounded-full border border-transparent bg-slate-100/80 pl-9 pr-3 text-[13px] text-slate-900 placeholder:text-slate-500 transition-all duration-150 ease-out-quart hover:bg-slate-100 focus:border-brand-500/60 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/15"
            />
          </div>

          {railQuery.trim() ? (
            /* Flat search results across every section. */
            <div className="space-y-0.5">
              {REPORTS.filter(
                (r) =>
                  (r.live && allowed(r.key)) &&
                  (r.title.toLowerCase().includes(railQuery.trim().toLowerCase()) ||
                    r.blurb.toLowerCase().includes(railQuery.trim().toLowerCase())),
              ).map((r) => (
                <RailRow key={r.key} r={r} active={selected === r.key} pinned={pinned.includes(r.key)}
                  onSelect={() => setSelected(r.key as ReportKey)} onTogglePin={() => togglePin(r.key)} />
              ))}
            </div>
          ) : (
            <>
              {/* Pinned + Recent ride the top of the rail. */}
              {pinned.length > 0 && (
                <div>
                  <div className="mb-1 flex items-center gap-1 px-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-amber-500">
                    <Star className="h-3 w-3 fill-current" /> Pinned
                  </div>
                  <div className="space-y-0.5">
                    {REPORTS.filter((r) => pinned.includes(r.key) && r.live && allowed(r.key)).map((r) => (
                      <RailRow key={r.key} r={r} active={selected === r.key} pinned
                        onSelect={() => setSelected(r.key as ReportKey)} onTogglePin={() => togglePin(r.key)} />
                    ))}
                  </div>
                </div>
              )}
              {recents.filter((k) => !pinned.includes(k)).length > 0 && (
                <div>
                  <div className="mb-1 flex items-center gap-1 px-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">
                    <History className="h-3 w-3" /> Recent
                  </div>
                  <div className="space-y-0.5">
                    {recents
                      .filter((k) => !pinned.includes(k))
                      .map((k) => REPORTS.find((r) => r.key === k))
                      .filter((r): r is ReportCard => Boolean(r && r.live && allowed(r.key)))
                      .map((r) => (
                        <RailRow key={r.key} r={r} active={selected === r.key} pinned={false}
                          onSelect={() => setSelected(r.key as ReportKey)} onTogglePin={() => togglePin(r.key)} />
                      ))}
                  </div>
                </div>
              )}
            </>
          )}

          {!railQuery.trim() && SECTIONS.map((section) => {
            // Per-report permissions: a report you can't run doesn't render;
            // a section with nothing visible disappears entirely.
            const visible = section.reports.filter(
              (r) => !r.live || allowed(r.key),
            );
            if (visible.length === 0) return null;
            const isCollapsed = collapsed.has(section.label);
            return (
            <div key={section.label}>
              <button
                type="button"
                onClick={() => toggleSection(section.label)}
                title={isCollapsed ? "Expand this group" : "Collapse this group"}
                className="mb-1 flex w-full items-center gap-1 rounded px-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400 transition-colors hover:text-slate-600"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                {section.label}
                {isCollapsed && (
                  <span className="ml-auto text-[10px] font-bold tabular-nums text-slate-300">
                    {visible.length}
                  </span>
                )}
              </button>
              {!isCollapsed && (
              <div className="space-y-0.5">
                {visible.map((r) => (
                  <RailRow
                    key={r.key}
                    r={r}
                    active={r.live && selected === r.key}
                    pinned={pinned.includes(r.key)}
                    onSelect={() => r.live && setSelected(r.key as ReportKey)}
                    onTogglePin={() => togglePin(r.key)}
                  />
                ))}
              </div>
              )}
            </div>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1">
          {selected === "ask" && (
            <>
              <AskPegasus />
              {/* The briefing lives on the landing page too (Seth 2026-09-02),
                  below the question box so Ask Pegasus stays the focus. */}
              {allowed("insights") && (
                <section className="mt-8 border-t border-hairline pt-6">
                  <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h2 className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-900">
                      <Lightbulb className="h-4 w-4 text-brand-600" /> Today&apos;s Briefing
                    </h2>
                    <button
                      type="button"
                      onClick={() => setSelected("insights")}
                      className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
                    >
                      Open the briefing on its own <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <InsightsPanel />
                </section>
              )}
            </>
          )}
          {selected === "insights" && <InsightsPanel />}
          {selected === "dispatch-list" && <DispatchListReport />}
          {selected === "invoice-list" && <InvoiceListReport />}
          {selected === "po-list" && <PurchaseOrderListReport />}
          {selected === "vendor-list" && <VendorListReport />}
          {selected === "ticket-list" && <TicketListReport />}
          {selected === "tech-payroll" && <TechPayrollReport />}
          {selected === "collections" && <CollectionsReport />}
          {selected === "membership-renewals" && <MembershipRenewalsReport />}
          {selected === "tuneups-due" && <TuneupsDueReport />}
          {selected === "parts-day" && <TruckPartsReport />}
          {selected === "customers" && <CustomersReport />}
          {selected === "serial-lookup" && <SerialLookupReport />}
          {selected === "warranty-expiring" && <WarrantyExpiringReport />}
          {selected === "refrigerant" && <RefrigerantReport />}
          {selected === "stock-counts" && <StockCountsReport />}
          {selected === "equipment-installs" && <EquipmentInstallsReport />}
          {selected === "seasonal-demand" && <SeasonalDemandReport />}
          {selected === "revenue" && <RevenueReport />}
          {selected === "lead-sources" && <LeadSourceReport />}
          {selected === "margin" && <MarginReport />}
          {selected === "dispatch" && <DispatchReport />}
          {selected === "agreements" && <CustomReport />}
          {selected === "classification-review" && <ClassificationReview />}
        </div>
      </div>
    </motion.div>
  );
}

/** One rail row — the star shows on hover (or always when pinned). */
function RailRow({
  r, active, pinned, onSelect, onTogglePin,
}: {
  r: ReportCard;
  active: boolean;
  pinned: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
}) {
  return (
    <div
      className={cn(
        "group flex w-full items-center gap-2 rounded-lg pr-1 transition-colors duration-150",
        active ? "bg-brand-50 ring-1 ring-inset ring-brand-100" : r.live ? "hover:bg-slate-100" : "",
      )}
    >
      <button
        type="button"
        disabled={!r.live}
        title={r.blurb}
        onClick={onSelect}
        className={cn(
          "flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 text-left text-[13.5px]",
          active
            ? "font-semibold text-brand-800"
            : r.live
              ? "font-medium text-slate-600 group-hover:text-slate-900"
              : "cursor-default text-slate-300",
        )}
      >
        <r.Icon
          className={cn(
            "h-4 w-4 shrink-0",
            active ? "text-brand-700" : r.live ? "text-slate-400 group-hover:text-slate-600" : "text-slate-300",
          )}
        />
        <span className="truncate">{r.title}</span>
        {!r.live && (
          <span className="ml-auto rounded bg-slate-100 px-1 text-[10px] font-bold uppercase text-slate-400">
            soon
          </span>
        )}
      </button>
      {r.live && (
        <button
          type="button"
          onClick={onTogglePin}
          title={pinned ? "Unpin" : "Pin to the top"}
          className={cn(
            "shrink-0 rounded p-0.5 transition-all",
            pinned
              ? "text-amber-400 hover:text-amber-500"
              : "text-slate-300 opacity-0 hover:text-amber-400 group-hover:opacity-100",
          )}
        >
          <Star className={cn("h-3.5 w-3.5", pinned && "fill-current")} />
        </button>
      )}
    </div>
  );
}
